import React, { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import { IconX } from '@tabler/icons-react';
import * as navigation from 'app-level/navigation';
import { FilterValue } from 'features/common-elements';
import { PortfolioFilterConfig } from 'features/pages/portfolio/Portfolio/portfolioFilters.config';
import { Button } from 'ui-kit';

import { AssetClass, Filter, FilterableField, FilterOperator } from '__generated__/globalTypes';
import { GetTapeLoanCount, GetTapeLoanCountVariables } from 'query/__generated__/getTapeLoanCount';
import { GET_TAPE_LOAN_COUNT_QUERY } from 'query/getTapeLoanCount';

import PoolFromTapeModal from './PoolFromTapeModal';
import {
  dismissPoolCandidateOnTable,
  LastPooledCandidate,
  readPoolCandidate,
} from './sessionPoolCandidate';
import { formatLoanCount, pluralizeLoans } from './tapePoolNaming';

const LatestUploadBanner = (): JSX.Element | null => {
  const gqlAssetClass: AssetClass | null = navigation.usePortfolioAssetClass();
  const [currentFilters, setFilters] = navigation.usePortfolioFilters();

  const [candidate, setCandidate] = useState<LastPooledCandidate | null>(null);
  const [hidden, setHidden] = useState(false);
  const [poolModalOpen, setPoolModalOpen] = useState(false);

  // Read sessionStorage on mount; we only need a snapshot for this render cycle.
  useEffect(() => {
    setCandidate(readPoolCandidate());
  }, []);

  const matchesAssetClass =
    candidate !== null && gqlAssetClass !== null && candidate.assetClass === gqlAssetClass;

  const tapeFilter: Filter[] = useMemo(() => {
    if (!candidate) return [];
    return [
      {
        field_name: FilterableField.last_loan_tape_id,
        operator: FilterOperator.IS,
        operand: candidate.tapeId,
        operandList: null,
      },
    ];
  }, [candidate]);

  const {
    data: countData,
    loading: countLoading,
    error: countError,
  } = useQuery<GetTapeLoanCount, GetTapeLoanCountVariables>(GET_TAPE_LOAN_COUNT_QUERY, {
    variables: { filters: tapeFilter },
    skip: !candidate || !matchesAssetClass,
    fetchPolicy: 'no-cache',
  });

  const loanCount = countData?.user?.company?.performance_summary?.loan_count ?? null;

  if (!candidate) return null;
  if (!matchesAssetClass) return null;
  if (candidate.dismissedOnTable) return null;
  if (hidden) return null;
  if (gqlAssetClass === null) return null;
  if (countError) return null;

  const handleFilterClick = () => {
    const tapeFilterValue: FilterValue<PortfolioFilterConfig> = {
      name: FilterableField.last_loan_tape_name,
      value: [candidate.tapeName],
    };
    const next = currentFilters.filter((f) => f.name !== FilterableField.last_loan_tape_name);
    setFilters([...next, tapeFilterValue]);
  };

  const handleDismiss = () => {
    dismissPoolCandidateOnTable();
    setHidden(true);
  };

  const countLabel = (() => {
    if (countLoading || loanCount === null) return '';
    return ` (${formatLoanCount(loanCount)} ${pluralizeLoans(loanCount)})`;
  })();
  // Disable when the count is unknown (null) too — clicking would otherwise
  // fire createPool with a filter that may return zero loans server-side
  // and produce an empty pool. Bugbot caught this on commit 6cfde245.
  const poolDisabled = countLoading || loanCount === null || loanCount === 0;

  return (
    <>
      <div
        className="mb-3 flex h-10 items-center justify-between gap-3 rounded-md border border-custom-purple bg-background-canvas px-3"
        data-testid="latest-upload-banner"
      >
        <div className="min-w-0 truncate text-[13px] text-foreground-default">
          Latest upload <strong className="font-semibold">{candidate.tapeName}</strong>
          {countLabel} is in this asset class.
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="secondary"
            size="small"
            label="filter to tape"
            onClick={handleFilterClick}
            data-testid="latest-upload-banner-filter"
          >
            Filter to this tape
          </Button>
          <Button
            type="primary_dark"
            size="small"
            label="pool from tape"
            disabled={poolDisabled}
            onClick={() => setPoolModalOpen(true)}
            data-testid="latest-upload-banner-pool"
          >
            Pool from this tape
          </Button>
          <button
            type="button"
            className="flex h-6 w-6 items-center justify-center rounded text-foreground-muted hover:bg-slate-700 hover:text-foreground-default"
            onClick={handleDismiss}
            data-testid="latest-upload-banner-dismiss"
            aria-label="Dismiss latest upload banner"
          >
            <IconX size={14} />
          </button>
        </div>
      </div>
      <PoolFromTapeModal
        open={poolModalOpen}
        onClose={() => setPoolModalOpen(false)}
        tape={{
          tapeId: candidate.tapeId,
          tapeName: candidate.tapeName,
          assetClass: gqlAssetClass,
        }}
      />
    </>
  );
};

export default LatestUploadBanner;
